import type { GetInviteEligibilityParams } from '@/modules/application-tracker/api';
import { applicantEmailValidate } from '@/modules/application-tracker/components/lease-form';

/** Fields the preflight reads. Edit rows also carry recipient identity; create rows only have email. */
export type InviteEligibilityApplicantRow = {
  email?: string | null;
  recipient_id?: string;
  original_email?: string;
  is_deleted?: boolean;
};

/**
 * Return false to leave a row out of the eligibility request.
 * The response `applicants[].index` is the position in the emails that were sent,
 * which the hook maps back to `formIndex`.
 */
export type InviteEligibilityRowFilter = (
  row: InviteEligibilityApplicantRow,
  formIndex: number,
) => boolean;

export type EligibilityEmailEntry = {
  formIndex: number;
  email: string;
};

export type EligibilityAddressFields = {
  property_alpha_id?: string | null;
  property_id?: string | null;
  address_alpha_id?: string | null;
  address_id?: string | null;
} | null;

export type EligibilityInputs = {
  propertyId: string;
  addressId: string;
  unit: string;
  emailEntries: EligibilityEmailEntry[];
  emails: string[];
  /** Invite being edited, left out of its own matches. Empty on create. */
  inviteAlphaId: string;
};

const trimOrEmpty = (value: string | null | undefined): string => value?.trim() || '';

/** Trimmed, lowercased email. Empty when the value is missing or blank. */
export const normalizeEligibilityEmail = (email: string | null | undefined): string =>
  trimOrEmpty(email).toLowerCase();

const resolvePropertyId = (address: EligibilityAddressFields | undefined): string =>
  trimOrEmpty(address?.property_alpha_id) || trimOrEmpty(address?.property_id);

const resolveAddressId = (address: EligibilityAddressFields | undefined): string =>
  trimOrEmpty(address?.address_alpha_id) || trimOrEmpty(address?.address_id);

/** Client-valid emails the preflight sends, in form order. */
const collectValidEligibilityEmails = (
  applicants: readonly InviteEligibilityApplicantRow[] | undefined,
  includeRow: InviteEligibilityRowFilter,
): EligibilityEmailEntry[] => {
  if (!applicants?.length) {
    return [];
  }

  const entries: EligibilityEmailEntry[] = [];
  applicants.forEach((row, formIndex) => {
    if (!includeRow(row, formIndex)) {
      return;
    }
    const email = normalizeEligibilityEmail(row?.email);
    if (email && applicantEmailValidate.isValidSync(email)) {
      entries.push({ formIndex, email });
    }
  });
  return entries;
};

/** Property, address, unit, and emails the eligibility query key is built from. */
export const buildEligibilityInputs = (
  address: EligibilityAddressFields | undefined,
  unit: unknown,
  applicants: readonly InviteEligibilityApplicantRow[] | undefined,
  includeRow: InviteEligibilityRowFilter,
  inviteAlphaId?: string,
): EligibilityInputs => {
  const emailEntries = collectValidEligibilityEmails(applicants, includeRow);
  return {
    propertyId: resolvePropertyId(address),
    addressId: resolveAddressId(address),
    unit: typeof unit === 'string' ? unit.trim() : '',
    emailEntries,
    emails: emailEntries.map((entry) => entry.email),
    inviteAlphaId: trimOrEmpty(inviteAlphaId),
  };
};

/** GET params for a complete preflight. Missing property, unit, or emails means no request. */
export const eligibilityQueryParams = (
  inputs: Pick<EligibilityInputs, 'propertyId' | 'addressId' | 'unit' | 'emails'> &
    Partial<Pick<EligibilityInputs, 'inviteAlphaId'>>,
): GetInviteEligibilityParams | undefined => {
  if (!inputs.propertyId || !inputs.unit || inputs.emails.length === 0) {
    return undefined;
  }
  return {
    property_id: inputs.propertyId,
    unit: inputs.unit,
    emails: inputs.emails,
    ...(inputs.addressId ? { address_id: inputs.addressId } : {}),
    ...(inputs.inviteAlphaId ? { invite_alpha_id: inputs.inviteAlphaId } : {}),
  };
};
